import type { StoreApi, UseBoundStore } from "zustand";
import type {
  DraftMachine,
  DraftParameter,
  DraftSensorLink,
  DraftCompatibility,
  DraftCanvasNode,
  DraftCanvasEdge,
  IdMap,
  TmpId,
} from "@opus-dt/shared-types";

export interface LineDraftHeader {
  name: string;
  description: string;
  responsible: string;
  // Spec 081 (FR-019) — a unidade em que a linha declara o que produz. Obrigatória no envio; o
  // rascunho começa vazia e o formulário recusa antes de chegar à rota.
  productionUnit: string;
}

export interface DraftCanvas {
  nodes: DraftCanvasNode[];
  edges: DraftCanvasEdge[];
}

export interface DraftLine {
  header: LineDraftHeader;
  canvas: DraftCanvas;
  machines: Record<string, DraftMachine>;
  dirty: boolean;
}

export interface DraftStoreState extends DraftLine {
  setHeader: (patch: Partial<LineDraftHeader>) => void;

  addMachine: (
    basic?: Partial<DraftMachine["basic"]>,
    position?: { x: number; y: number }
  ) => TmpId;
  cancelPendingMachine: (tmpMachineId: string) => void;
  removeMachine: (tmpMachineId: string) => void;
  updateMachineBasic: (tmpMachineId: string, patch: Partial<DraftMachine["basic"]>) => void;

  addParameter: (tmpMachineId: string, param: Omit<DraftParameter, "tmpId">) => TmpId;
  updateParameter: (
    tmpMachineId: string,
    tmpParamId: string,
    patch: Partial<DraftParameter>
  ) => void;
  removeParameter: (tmpMachineId: string, tmpParamId: string) => void;
  addSensorLink: (tmpMachineId: string, link: Omit<DraftSensorLink, "tmpId">) => TmpId;
  removeSensorLink: (tmpMachineId: string, tmpLinkId: string) => void;
  addCompatibility: (tmpMachineId: string, compat: Omit<DraftCompatibility, "tmpId">) => TmpId;
  // Edita o par já existente (perfil de simulação por produto) sem recriá-lo.
  updateCompatibility: (
    tmpMachineId: string,
    tmpCompatId: string,
    patch: Partial<Omit<DraftCompatibility, "tmpId">>
  ) => void;
  removeCompatibility: (tmpMachineId: string, tmpCompatId: string) => void;

  addEdge: (sourceNodeTmpId: string, targetNodeTmpId: string) => TmpId;
  removeEdge: (tmpEdgeId: string) => void;
  setCanvas: (canvas: DraftCanvas) => void;

  loadFromSession: (draft: DraftLine) => void;
  reset: () => void;
  applyIdMap: (idMap: IdMap) => void;
}

export type DraftStore = UseBoundStore<StoreApi<DraftStoreState>>;

export const INITIAL_DRAFT: DraftLine = {
  header: { name: "", description: "", responsible: "", productionUnit: "" },
  canvas: { nodes: [], edges: [] },
  machines: {},
  dirty: false,
};

export const DEFAULT_BASIC: DraftMachine["basic"] = {
  code: "",
  name: "",
  type: "MACHINE",
  requiresOperator: false,
};
